"use client"

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { usePreferences } from "@/contexts/preferences-context"
import { confirmDialogTranslations } from "@/dictionaries/common/confirm-dialog"

type ConfirmDialogOptions = {
  title?: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: "default" | "destructive"
}

type PromptDialogOptions = ConfirmDialogOptions & {
  placeholder?: string
  defaultValue?: string
  inputType?: "text" | "password"
}

type AlertDialogOptions = {
  title?: string
  description?: string
  confirmText?: string
}

type ConfirmDialogContextValue = {
  confirm: (options?: ConfirmDialogOptions) => Promise<boolean>
  prompt: (options?: PromptDialogOptions) => Promise<string | null>
  alert: (options?: AlertDialogOptions) => Promise<void>
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null)

type DialogMode = "confirm" | "prompt" | "alert"

type ConfirmDialogState = Required<ConfirmDialogOptions> & {
  open: boolean
  mode: DialogMode
  inputType: "text" | "password"
  placeholder: string
  inputValue: string
}

const DEFAULT_STATE: ConfirmDialogState = {
  open: false,
  mode: "confirm",
  title: "Confirm action",
  description: "Are you sure you want to continue?",
  confirmText: "OK",
  cancelText: "Cancel",
  variant: "default",
  inputType: "text",
  placeholder: "",
  inputValue: "",
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const { preferences } = usePreferences()
  const lang = (preferences.language ?? "ro") as keyof typeof confirmDialogTranslations
  const t = confirmDialogTranslations[lang] ?? confirmDialogTranslations.ro

  const [state, setState] = useState<ConfirmDialogState>(DEFAULT_STATE)
  const resolverRef = useRef<((value: unknown) => void) | null>(null)

  const resolveAndClose = useCallback((value: unknown) => {
    setState((prev) => ({ ...prev, open: false }))
    resolverRef.current?.(value)
    resolverRef.current = null
  }, [])

  const confirm = useCallback((options?: ConfirmDialogOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve as unknown as (value: unknown) => void
      setState({
        open: true,
        mode: "confirm",
        title: options?.title || t.defaultTitle,
        description: options?.description || t.defaultDescription,
        confirmText: options?.confirmText || t.confirmText,
        cancelText: options?.cancelText || t.cancelText,
        variant: options?.variant || DEFAULT_STATE.variant,
        inputType: "text",
        placeholder: "",
        inputValue: "",
      })
    })
  }, [t])

  const prompt = useCallback((options?: PromptDialogOptions) => {
    return new Promise<string | null>((resolve) => {
      resolverRef.current = resolve as unknown as (value: unknown) => void
      setState({
        open: true,
        mode: "prompt",
        title: options?.title || t.defaultTitle,
        description: options?.description || t.defaultDescription,
        confirmText: options?.confirmText || t.confirmText,
        cancelText: options?.cancelText || t.cancelText,
        variant: options?.variant || DEFAULT_STATE.variant,
        inputType: options?.inputType || "text",
        placeholder: options?.placeholder || "",
        inputValue: options?.defaultValue || "",
      })
    })
  }, [t])

  const alert = useCallback((options?: AlertDialogOptions) => {
    return new Promise<void>((resolve) => {
      resolverRef.current = () => resolve()
      setState({
        open: true,
        mode: "alert",
        title: options?.title || t.defaultTitle,
        description: options?.description || t.defaultDescription,
        confirmText: options?.confirmText || t.confirmText,
        cancelText: t.cancelText,
        variant: "default",
        inputType: "text",
        placeholder: "",
        inputValue: "",
      })
    })
  }, [t])

  const contextValue = useMemo<ConfirmDialogContextValue>(() => ({ confirm, prompt, alert }), [confirm, prompt, alert])

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}

      <Dialog
        open={state.open}
        onOpenChange={(open) => {
          if (!open) {
            if (state.mode === "confirm") resolveAndClose(false)
            else if (state.mode === "prompt") resolveAndClose(null)
            else resolveAndClose(undefined)
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] sm:max-w-md p-4 sm:p-6 max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="leading-tight break-words [overflow-wrap:anywhere]">{state.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{state.description}</DialogDescription>
          </DialogHeader>

          {state.mode === "prompt" && (
            <Input
              type={state.inputType}
              value={state.inputValue}
              onChange={(e) => setState((prev) => ({ ...prev, inputValue: e.target.value }))}
              placeholder={state.placeholder}
            />
          )}

          <DialogFooter className="w-full !grid grid-cols-1 sm:grid-cols-2 gap-2">
            {state.mode !== "alert" && (
              <Button
                type="button"
                variant="outline"
                className="w-full min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-center h-auto min-h-10 py-2 shrink"
                onClick={() => resolveAndClose(state.mode === "confirm" ? false : null)}
              >
                {state.cancelText}
              </Button>
            )}
            <Button
              type="button"
              variant={state.variant === "destructive" ? "destructive" : "default"}
              className="w-full min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-center h-auto min-h-10 py-2 shrink"
              onClick={() => {
                if (state.mode === "confirm") {
                  resolveAndClose(true)
                  return
                }
                if (state.mode === "prompt") {
                  const value = state.inputValue.trim()
                  resolveAndClose(value || null)
                  return
                }
                resolveAndClose(undefined)
              }}
            >
              {state.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmDialogContext.Provider>
  )
}

export function useAppConfirm() {
  const context = useContext(ConfirmDialogContext)
  if (!context) {
    throw new Error("useAppConfirm must be used within ConfirmDialogProvider")
  }
  return context.confirm
}

export function useAppPrompt() {
  const context = useContext(ConfirmDialogContext)
  if (!context) {
    throw new Error("useAppPrompt must be used within ConfirmDialogProvider")
  }
  return context.prompt
}

export function useAppAlert() {
  const context = useContext(ConfirmDialogContext)
  if (!context) {
    throw new Error("useAppAlert must be used within ConfirmDialogProvider")
  }
  return context.alert
}
